"use client";
import ArticleBody from "@/components/ArticleBody";
import { articlePresentation } from "@/lib/articles/presentation";
import ArticleImageCaption from "@/components/ArticleImageCaption";

import { normalizeTags } from "@/lib/articles/tags";
import React, { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import ArticleAuthorLinks from '@/components/ArticleAuthorLinks';
import { Link as LinkIcon, MessageCircle, AtSign } from 'lucide-react';
import ArticleSidebar from '@/components/ArticleSidebar';
import TechnologySidebar from '@/theme-library/technology/1.0.0/components/TechnologySidebar';
import AdSlotWrapper from '@/components/AdSlotWrapper';
import GameWidget from '@/components/games/GameWidget';


const CommentNode = ({ comment, session, replyingTo, setReplyingTo, replyText, setReplyText, handlePostReply, handleLikeComment, submittingReply, likingCommentId, depth = 0 }) => {
  const author = comment.reader?.displayName || 'Deleted User';
  return (
    <div className={`p-4 rounded border border-gray-800 ${depth === 0 ? 'bg-[#1a1918]' : 'bg-brand-secondary mt-3'}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 rounded-full bg-brand flex items-center justify-center font-bold text-black text-xs uppercase">
          {comment.reader?.avatar
            ? <img src={comment.reader.avatar} alt="" className="h-full w-full rounded-full object-cover" />
            : author.charAt(0)}
        </div>
        <span className="text-white text-sm font-bold">{author}</span>
        <span className="text-gray-500 text-[10px] ml-auto">{new Date(comment.createdAt).toLocaleString()}</span>
      </div>
      <p className="text-[#cac3ad] text-sm whitespace-pre-line">{comment.body}</p>
      
      {/* Actions */}
      <div className="flex gap-4 mt-3">
        <button onClick={() => handleLikeComment(comment.id)} disabled={!session || likingCommentId === comment.id} className="text-gray-400 text-xs font-bold hover:text-brand transition-colors flex items-center gap-1 disabled:cursor-not-allowed disabled:opacity-50">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.514M9 11l-2 2"></path></svg>
          {comment.likeCount || 0}
        </button>
        {depth === 0 && session?.status === 'ACTIVE' && <button onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)} className="text-gray-400 text-xs font-bold hover:text-white transition-colors flex items-center gap-1">
          <MessageCircle className="w-3 h-3" /> Reply
        </button>}
      </div>

      {/* Reply Form */}
      {depth === 0 && replyingTo === comment.id && session?.status === 'ACTIVE' && (
        <div className="mt-4 pl-4 md:pl-8 border-l-2 border-gray-700">
          <textarea 
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            maxLength={2000}
            className="w-full bg-brand-secondary border border-gray-700 rounded p-2 text-white text-sm focus:outline-none focus:border-brand min-h-[60px] mb-2 resize-none" 
            placeholder="Write a reply..."
          ></textarea>
          <div className="flex justify-end gap-2">
            <button onClick={() => setReplyingTo(null)} className="text-gray-400 text-xs hover:text-white">Cancel</button>
            <button onClick={() => handlePostReply(comment.id)} disabled={submittingReply || !replyText.trim()} className="bg-brand text-black px-4 py-1 text-xs font-bold rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50">{submittingReply ? 'Posting…' : 'Post Reply'}</button>
          </div>
        </div>
      )}

      {/* Nested Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-4 pl-4 md:pl-8 border-l-2 border-gray-800 flex flex-col">
          {comment.replies.map(reply => (
            <CommentNode 
              key={reply.id} 
              comment={reply} 
              session={session}
              replyingTo={replyingTo}
              setReplyingTo={setReplyingTo}
              replyText={replyText}
              setReplyText={setReplyText}
              handlePostReply={handlePostReply}
              handleLikeComment={handleLikeComment}
              submittingReply={submittingReply}
              likingCommentId={likingCommentId}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Article = ({ initialArticle, settings = {}, latestArticles = [], publicationId }) => {
  const pathname = usePathname();
  const siteSettings = settings;
  
  const [commentText, setCommentText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  
  const publishedArticles = latestArticles;
  const article = initialArticle || {};
  const isTechnology = ['Technology', 'technology', 'technology@1.0.0'].includes(settings?.templateId);
  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [totalCommentsCount, setTotalCommentsCount] = useState(0);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentsError, setCommentsError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [submittingReply, setSubmittingReply] = useState(false);
  const [likingCommentId, setLikingCommentId] = useState(null);

  const loadComments = useCallback(async () => {
    if (!article.id || !publicationId) {
      setCommentsLoading(false);
      return;
    }
    setCommentsLoading(true);
    setCommentsError('');
    try {
      const params = new URLSearchParams({ articleId: article.id, publicationId });
      const response = await fetch(`/api/comments?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load comments');
      setComments(data.comments || []);
      setTotalCommentsCount(data.count || 0);
    } catch (error) {
      setCommentsError(error.message || 'Failed to load comments');
    } finally {
      setCommentsLoading(false);
    }
  }, [article.id, publicationId]);

  useEffect(() => {
    fetch('/api/reader/auth/session')
      .then(async response => response.ok ? response.json() : null)
      .then(data => setSession(data?.reader || null))
      .catch(() => setSession(null))
      .finally(() => setSessionLoading(false));
  }, []);

  useEffect(() => {
    if (article.id && publicationId) loadComments();
  }, [article.id, publicationId, loadComments]);

  const postComment = async ({ body, parentId = null }) => {
    const response = await fetch('/api/comments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ articleId: article.id, publicationId, body, parentId }),
    });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 401) setSession(null);
      throw new Error(data.error || 'Unable to submit comment');
    }
    setSubmitMessage(data.message || 'Comment submitted.');
    await loadComments();
  };

  const handlePostComment = async () => {
    const body = commentText.trim();
    if (!body || submittingComment) return;
    setSubmittingComment(true);
    setSubmitMessage('');
    try {
      await postComment({ body });
      setCommentText('');
    } catch (error) {
      setSubmitMessage(error.message || 'Unable to submit comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handlePostReply = async commentId => {
    const body = replyText.trim();
    if (!body || submittingReply) return;
    setSubmittingReply(true);
    setSubmitMessage('');
    try {
      await postComment({ body, parentId: commentId });
      setReplyText('');
      setReplyingTo(null);
    } catch (error) {
      setSubmitMessage(error.message || 'Unable to submit reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleLikeComment = async commentId => {
    if (!session || likingCommentId) {
      if (!session) setSubmitMessage('Sign in to like comments.');
      return;
    }
    setLikingCommentId(commentId);
    setSubmitMessage('');
    try {
      const response = await fetch('/api/comments/like', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ commentId }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401) setSession(null);
        throw new Error(data.error || 'Unable to update like');
      }
      await loadComments();
    } catch (error) {
      setSubmitMessage(error.message || 'Unable to update like');
    } finally {
      setLikingCommentId(null);
    }
  };

  const returnTo = pathname || `/${article.slug}`;
  const loginUrl = `/reader/login?returnTo=${encodeURIComponent(returnTo)}`;
  const registerUrl = `/reader/register?returnTo=${encodeURIComponent(returnTo)}`;

  if (!initialArticle) return null;

  // Dynamic recommendation algorithm based on keywords/tags
  const getRelatedArticles = () => {
    const getTagsArray = normalizeTags;

    return latestArticles
      .filter(a => a.id !== article.id)
      .map(a => {
        let score = 0;
        
        // 1. Category match (+5 points)
        if (a.category === article.category) score += 5;
        
        // 2. Tag matches (+3 points each)
        const aTags = getTagsArray(a.tags);
        const articleTags = getTagsArray(article.tags);
        const commonTags = aTags.filter(tag => articleTags.includes(tag));
        score += commonTags.length * 3;
        
        // 3. Keyword matches in title (+1 point each, ignoring short words)
        const currentWords = article.title.toLowerCase().split(' ');
        const aWords = a.title.toLowerCase().split(' ');
        const commonWords = aWords.filter(word => word.length > 3 && currentWords.includes(word));
        score += commonWords.length;

        return { ...a, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 6); // Take top 6 highest scoring articles
  };

  const relatedArticles = getRelatedArticles();
  const presentation = articlePresentation(article);

  return (
    <div className="w-full">
      <AdSlotWrapper id="article_top_1" className="mb-8" style={{ minHeight: '90px' }}  settings={settings} />
      
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 relative">
        {/* Main Content Area */}
        <div className="lg:col-span-8 flex flex-col">
          
          <article className="w-full">
            {/* Header / Byline */}
            <header className="mb-8">
              <Link href={`/category/${article.category.toLowerCase()}`} className="inline-block bg-gray-900 text-white px-3 py-1 text-xs font-bold uppercase tracking-widest mb-4 hover:bg-brand transition-colors">
                {article.category}
              </Link>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight tracking-tighter text-gray-900 mb-6 font-sans">
                {article.title}
              </h1>
              {presentation.subtitle && <p data-article-subtitle className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-6">{presentation.subtitle}</p>}
              
              <div className="flex flex-col md:flex-row md:items-center justify-between py-4 border-y border-gray-200 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                    <img src="https://ui-avatars.com/api/?name=Editorial+Team&background=121111&color=fff" alt="Author" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    {isTechnology ? <div className="font-bold text-gray-900 text-sm uppercase tracking-wide hover:text-brand transition-colors block">By <ArticleAuthorLinks article={article} /></div> : <span className="font-bold text-gray-900 text-sm uppercase tracking-wide hover:text-brand transition-colors block">By <ArticleAuthorLinks article={article} /></span>}
                    <time className="text-gray-500 text-sm">
                      {new Intl.DateTimeFormat(settings?.publicationLocale || 'en-US', { timeZone: settings?.publicationTimeZone || 'UTC', month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(article.date))} at {new Intl.DateTimeFormat(settings?.publicationLocale || 'en-US', { timeZone: settings?.publicationTimeZone || 'UTC', hour: 'numeric', minute: '2-digit' }).format(new Date(article.date))}
                    </time>
                  </div>
                </div>

                {/* Social Share */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-widest mr-2">Share:</span>
                  
                  {/* Facebook */}
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent((typeof window !== 'undefined' ? window.location.href : `https://surgedome.local/${article.slug}`))}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-brand-secondary text-white flex items-center justify-center shadow-[0_4px_8px_rgba(0,0,0,0.4)] hover:-translate-y-1 hover:shadow-[0_6px_12px_rgba(0,0,0,0.6)] hover:bg-brand transition-all duration-300" title="Share on Facebook">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                  
                  {/* X (Twitter) */}
                  <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent((typeof window !== 'undefined' ? window.location.href : `https://surgedome.local/${article.slug}`))}&text=${encodeURIComponent(article.title)}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-brand-secondary text-white flex items-center justify-center shadow-[0_4px_8px_rgba(0,0,0,0.4)] hover:-translate-y-1 hover:shadow-[0_6px_12px_rgba(0,0,0,0.6)] hover:bg-brand transition-all duration-300" title="Share on X">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                  </a>

                  {/* Threads */}
                  <a href={`https://threads.net/intent/post?text=${encodeURIComponent(article.title +  ' ' + (typeof window !== 'undefined' ? window.location.href : `https://surgedome.local/${article.slug}`))}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-brand-secondary text-white flex items-center justify-center shadow-[0_4px_8px_rgba(0,0,0,0.4)] hover:-translate-y-1 hover:shadow-[0_6px_12px_rgba(0,0,0,0.6)] hover:bg-brand transition-all duration-300" title="Share on Threads">
                    <AtSign className="w-4 h-4" />
                  </a>

                  {/* LinkedIn */}
                  <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent((typeof window !== 'undefined' ? window.location.href : `https://surgedome.local/${article.slug}`))}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-brand-secondary text-white flex items-center justify-center shadow-[0_4px_8px_rgba(0,0,0,0.4)] hover:-translate-y-1 hover:shadow-[0_6px_12px_rgba(0,0,0,0.6)] hover:bg-brand transition-all duration-300" title="Share on LinkedIn">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                  </a>

                  {/* WhatsApp */}
                  <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(article.title +  ' ' + (typeof window !== 'undefined' ? window.location.href : `https://surgedome.local/${article.slug}`))}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-brand-secondary text-white flex items-center justify-center shadow-[0_4px_8px_rgba(0,0,0,0.4)] hover:-translate-y-1 hover:shadow-[0_6px_12px_rgba(0,0,0,0.6)] hover:bg-brand transition-all duration-300" title="Share on WhatsApp">
                    <MessageCircle className="w-4 h-4" />
                  </a>

                  {/* Copy Link */}
                  <button onClick={() => { navigator.clipboard.writeText((typeof window !== 'undefined' ? window.location.href : `https://surgedome.local/${article.slug}`)); alert('Link copied!'); }} className="w-9 h-9 rounded-full bg-brand-secondary text-white flex items-center justify-center shadow-[0_4px_8px_rgba(0,0,0,0.4)] hover:-translate-y-1 hover:shadow-[0_6px_12px_rgba(0,0,0,0.6)] hover:bg-brand transition-all duration-300" title="Copy Link">
                    <LinkIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </header>

            {/* Hero Image */}
            {presentation.image && <figure className="mb-8">
              <div className="w-full aspect-[16/9] bg-gray-200 relative">
                <img src={presentation.image} alt={presentation.imageAlt} className="w-full h-full object-cover" />
              </div>
              <ArticleImageCaption article={article} className="bg-black/60 p-2 text-white text-xs text-right" />
            </figure>}

            {/* Article Content */}
            <ArticleBody publicationSettings={settings} className="prose prose-xl max-w-none text-black prose-p:text-black prose-headings:text-black prose-blockquote:text-black prose-blockquote:font-sans prose-blockquote:not-italic font-sans leading-relaxed mb-12" html={article.content} />
            
            {/* Dynamic Overflow Zone for Newly Generated Inline Slots */}
              {Object.keys(siteSettings?.ads?.slots || {})
                .filter(id => id.startsWith('article_inline_') && parseInt(id.replace('article_inline_', '')) > 1)
                .sort((a, b) => parseInt(a.replace('article_inline_', '')) - parseInt(b.replace('article_inline_', '')))
                .map(id => (
                  <div key={id} className="my-10 flex flex-col items-center">
                    <span className="text-[10px] uppercase tracking-widest text-gray-400 block text-center mb-3">Advertisement</span>
                    <AdSlotWrapper id={id} className="w-full" style={{ minHeight: '250px' }}  settings={settings} />
                  </div>
              ))}

            
            {/* Article Footer Tags */}
            <div className="flex flex-wrap gap-2 mb-10 pb-10 border-b border-gray-200">
              <span className="font-bold text-sm mr-2 text-gray-900">Tags:</span>
              {(article.tags || ['News', 'Global', 'Trending']).map((tag, i) => (
                <a key={i} href="#" className="bg-gray-100 px-3 py-1 text-sm font-medium hover:bg-[var(--brand-surface-hover)] transition-colors capitalize">
                  {tag}
                </a>
              ))}
            </div>

            {/* Centered Brand Logo */}
            <div className="flex items-center justify-center gap-1.5 mb-12">
              <span className="text-2xl font-black tracking-tighter text-gray-900 mt-1" style={{ fontFamily: '"Audiowide", sans-serif' }}>{typeof window !== 'undefined' ? window.location.host.split('.')[0] : 'SurgeDome'}</span>
            </div>

            {/* Ad space before Related Articles */}
            <div className="my-8 w-full">
              <AdSlotWrapper id="article_before_related" style={{ minHeight: '90px' }}  settings={settings} />
            </div>

            {/* Related Articles */}
            <div className="mb-12 border-t border-gray-200 pt-8">
              <h3 className="text-2xl font-bold mb-6 text-gray-900 border-l-4 border-brand pl-3">Related Articles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedArticles.map((related) => (
                  <Link key={related.id} href={`/article/${related.slug}`} className="group flex gap-4 items-center bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-2">
                    <div className="w-[120px] h-[90px] flex-shrink-0 bg-gray-200 overflow-hidden">
                      <img src={related.image} alt={related.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-brand uppercase tracking-wider mb-1">{related.category}</span>
                      <h4 className="text-base font-bold leading-snug text-gray-900 group-hover:text-brand transition-colors line-clamp-2">
                        {related.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Ad space before Comment Section */}
            <div className="my-8 w-full">
              <AdSlotWrapper id="article_before_comments" style={{ minHeight: '90px' }}  settings={settings} />
            </div>

            {/* Join Community / Comments Section */}
            <div className="mb-12 bg-white p-10 flex flex-col items-center justify-center text-center shadow-[0_4px_20px_rgba(0,0,0,0.05)] border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-brand"></div>
              
              {/* Logo Image */}
              {/* Dynamic Logo handled by parent, or default to text */}
              
              <h3 className="text-3xl font-black mb-3 text-[#121111] tracking-tight">Join the Conversation</h3>
              <p className="text-[#121111] mb-8 max-w-md text-sm leading-relaxed font-medium">
                Sign in or join the community to share your opinion, engage with authors, and interact with other readers.
              </p>
              
              {!sessionLoading && !session ? (
                <div className="flex gap-4">
                  <Link href={loginUrl} className="bg-brand-secondary text-white px-8 py-3 text-sm font-bold uppercase tracking-widest hover:bg-brand hover:text-[#121111] transition-colors">
                    Sign In
                  </Link>
                  <Link href={registerUrl} className="bg-transparent border border-[#121111] text-[#121111] px-8 py-3 text-sm font-bold uppercase tracking-widest hover:bg-brand-secondary hover:text-white transition-colors">
                    Join Free
                  </Link>
                </div>
              ) : session?.status === 'ACTIVE' ? (
                <div className="w-full max-w-lg mt-4 bg-[#1a1918] p-4 rounded-lg border border-gray-800 text-left">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center font-bold text-black text-sm uppercase">
                      {session.displayName.charAt(0)}
                    </div>
                    <span className="text-white text-sm font-bold">Commenting as {session.displayName}</span>
                  </div>
                  <textarea 
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    maxLength={2000}
                    className="w-full bg-brand-secondary border border-gray-700 rounded p-3 text-white text-sm focus:outline-none focus:border-brand min-h-[100px] mb-3 resize-none" 
                    placeholder="Share your thoughts..."
                  ></textarea>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-gray-400">{commentText.length}/2000</span>
                    <button 
                      onClick={handlePostComment}
                      disabled={submittingComment || !commentText.trim()}
                      className="bg-brand text-black px-6 py-2 text-sm font-bold rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submittingComment ? 'Posting…' : 'Post Comment'}
                    </button>
                  </div>
                </div>
              ) : sessionLoading ? (
                <p className="text-sm text-gray-500">Checking reader session…</p>
              ) : (
                <p className="text-sm text-amber-700">Your reader account must be active before commenting.</p>
              )}

              {submitMessage && <p role="status" className="mt-4 text-sm font-medium text-[#121111]">{submitMessage}</p>}

              {/* Display Comments */}
              <div className="w-full max-w-lg mt-6 text-left border-t border-gray-800 pt-6">
                  <h4 className="text-[#121111] font-bold mb-4">{totalCommentsCount} Comment{totalCommentsCount !== 1 ? 's' : ''}</h4>
                  {commentsLoading ? (
                    <p className="text-sm text-gray-500">Loading comments…</p>
                  ) : commentsError ? (
                    <div className="flex items-center justify-between gap-4 text-sm text-red-700">
                      <span>{commentsError}</span>
                      <button type="button" onClick={loadComments} className="font-bold underline">Try again</button>
                    </div>
                  ) : comments.length === 0 ? (
                    <p className="text-sm text-gray-500">No published comments yet. Be the first to share your thoughts.</p>
                  ) : (
                  <div className="flex flex-col gap-4">
                    {comments.map(c => (
                      <CommentNode 
                        key={c.id} 
                        comment={c} 
                        session={session}
                        replyingTo={replyingTo}
                        setReplyingTo={setReplyingTo}
                        replyText={replyText}
                        setReplyText={setReplyText}
                        handlePostReply={handlePostReply}
                        handleLikeComment={handleLikeComment}
                        submittingReply={submittingReply}
                        likingCommentId={likingCommentId}
                        depth={0}
                      />
                    ))}
                  </div>
                  )}
                </div>
            </div>

            {/* Ad space before Games section */}
            <div className="my-8 w-full">
              <AdSlotWrapper id="article_before_games" style={{ minHeight: '90px' }}  settings={settings} />
            </div>

            {/* Games Widget */}
            <div className="mb-12 border-t border-b border-gray-200 py-10 mt-10">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-black italic tracking-tighter uppercase text-gray-900">Take a Break</h3>
                <p className="text-gray-500 font-medium">Flex your brain with our daily mini-game</p>
              </div>
              <GameWidget />
            </div>

            {/* Taboola Sponsored Widget */}
            <AdSlotWrapper id="taboola_article_bottom" style={{ minHeight: '300px' }}  settings={settings} />

          </article>
        </div>

        {/* Right Sidebar Area */}
        <div className="lg:col-span-4">
          <div className="sticky top-[120px]">
              {isTechnology
                ? <TechnologySidebar articles={publishedArticles} settings={settings} context="article" />
                : <ArticleSidebar articles={publishedArticles} settings={settings} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Article;
